import {useEffect} from 'react';

import {isEmpty, isNil} from 'ramda';

import {omneticApi} from '@dms/api/base';

import {AppSyncMessage, SubscribeToNotificationsApiArg} from './types';
import {buildWebSocketUrl} from './utils/buildWebSocketUrl';
import {captureWebsocketError} from './utils/captureWebsocketError';
import {createSubscriptionMessage} from './utils/createSubscriptionMessage';
import {getSubscriptionId} from './utils/getSubscriptionId';
import {handleIncomingData} from './utils/handleIncomingData';
import {handleWebSocketMessage} from './utils/handleWebSocketMessage';

const WEBSOCKET_PROTOCOL = 'graphql-ws';
const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;
const MAX_CACHE_SIZE = 50;

export const appSyncApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    subscribeToNotifications: build.query<SubscribeToNotificationsApiArg[], void>({
      queryFn: () => ({
        data: [],
      }),
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(args, {cacheEntryRemoved, getState, dispatch, updateCachedData}) {
        const subscriptionId = getSubscriptionId(getState());
        let keepAliveTimeoutMs = FIVE_MINUTES_IN_MS;
        let keepAliveTimeout: ReturnType<typeof setTimeout>;

        let ws: WebSocket;

        try {
          const url = buildWebSocketUrl();

          if (isNil(url)) {
            return;
          }

          ws = new WebSocket(url, [WEBSOCKET_PROTOCOL]);
        } catch (error) {
          captureWebsocketError(error);
          await cacheEntryRemoved;
          return;
        }

        const resetKeepAlive = () => {
          clearTimeout(keepAliveTimeout);
          keepAliveTimeout = setTimeout(() => {
            if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
              ws.close();
            }
          }, keepAliveTimeoutMs);
        };

        try {
          ws.onopen = () => ws.send(JSON.stringify({type: 'connection_init'}));
          ws.onerror = captureWebsocketError;
          ws.onclose = () => {
            keepAliveTimeoutMs = FIVE_MINUTES_IN_MS;
            clearTimeout(keepAliveTimeout);
          };

          ws.onmessage = (event) => {
            try {
              const message = JSON.parse(event.data) as AppSyncMessage;

              handleWebSocketMessage(message, {
                onConnectionAck: (keepAliveMs) => {
                  const subscriptionMessage = createSubscriptionMessage(subscriptionId);

                  if (isNil(subscriptionMessage)) {
                    return;
                  }

                  ws.send(JSON.stringify(subscriptionMessage));
                  keepAliveTimeoutMs = keepAliveMs;
                  resetKeepAlive();
                },
                onData: handleIncomingData(dispatch, (notification) =>
                  updateCachedData((draft) => {
                    if (isNil(draft)) {
                      return [notification];
                    }

                    if (draft.length >= MAX_CACHE_SIZE) {
                      return [...draft.slice(1), notification];
                    }

                    return [...draft, notification];
                  })
                ),
                onError: captureWebsocketError,
                onComplete: ws.close,
                onKeepAlive: resetKeepAlive,
              });
            } catch (error) {
              captureWebsocketError(error);
            }
          };
        } catch (error) {
          captureWebsocketError(error);
        }

        // is "pending" until the hook is unmounted
        await cacheEntryRemoved;

        if (ws.readyState === WebSocket.OPEN) {
          ws.send(
            JSON.stringify({
              id: subscriptionId,
              type: 'stop',
            })
          );
          ws.close();
        }
      },
    }),
  }),
});

export const useSubscribeToNotificationsQuery = (
  onData: (notification: SubscribeToNotificationsApiArg) => void
) => {
  const {data: latestData} = appSyncApi.useSubscribeToNotificationsQuery();

  useEffect(() => {
    if (isNil(latestData) || isEmpty(latestData)) {
      return;
    }

    onData(latestData[latestData.length - 1]);
  }, [latestData]);

  return [latestData];
};
